package com.evoapps.evoshift.ui.user

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.LabourBase
import com.evoapps.evoshift.ui.util.labourShareChartRes
import com.evoapps.evoshift.ui.finance.formatPercent
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.usecase.finance.DayTrend
import com.evoapps.evoshift.domain.usecase.finance.MonthTrend
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.finance.formatDecimal
import com.evoapps.evoshift.ui.finance.formatMoney
import com.evoapps.evoshift.ui.util.dayShort
import com.evoapps.evoshift.ui.util.monthName
import com.evoapps.evoshift.viewmodels.DayHours
import com.evoapps.evoshift.widgets.BarLineChart
import com.evoapps.evoshift.widgets.ChartBars
import com.evoapps.evoshift.widgets.ChartFigure
import com.evoapps.evoshift.widgets.ChartLine
import com.evoapps.evoshift.widgets.RegularCard
import com.evoapps.evoshift.widgets.compactAmount
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.chart_hours_planned
import evoshift.composeapp.generated.resources.chart_hours_title
import evoshift.composeapp.generated.resources.chart_hours_worked
import evoshift.composeapp.generated.resources.chart_labor_cost
import evoshift.composeapp.generated.resources.chart_labor_trend_title
import evoshift.composeapp.generated.resources.chart_plan_suffix
import evoshift.composeapp.generated.resources.chart_revenue
import evoshift.composeapp.generated.resources.chart_revenue_labor_title
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringResource
import kotlin.math.ceil

private val LaborShareRed = Color(0xFFC0392B)

/** The labour share that most hospitality owners aim to stay under. */
private const val LABOR_TARGET_PERCENT = 30.0

/** The share axis reaches at least 10 points above the target line (40% for the usual 30%). */
private fun shareAxisMax(reference: Double): Double = maxOf(40.0, reference + 10.0)

private fun percent(v: Double) = formatPercent(v)

/** Labels every n-th slot so they never overlap (about 24dp per label). */
private fun labelStep(width: Dp, slots: Int): Int =
    if (slots == 0) 1 else ceil(24.0 / ((width.value - 76) / slots).coerceAtLeast(1f)).toInt().coerceAtLeast(1)

private fun todayIndex(dates: List<LocalDate>): Int? {
    val today = AppClock.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
    return dates.indexOf(today).takeIf { it >= 0 }
}

@Composable
private fun dayLabel(date: LocalDate) = "${dayShort(date.dayOfWeek)} ${date.day}.${date.month.number.toString().padStart(2, '0')}"

@Composable
private fun ChartCard(
    title: String,
    modifier: Modifier = Modifier,
    /** Beside the title, e.g. a button to show the chart full size. */
    action: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit
) {
    RegularCard(modifier = modifier.fillMaxWidth()) {
        Column(Modifier.padding(20.dp)) {
            Row(verticalAlignment = androidx.compose.ui.Alignment.CenterVertically) {
                Text(title, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = DeepNavy, modifier = Modifier.weight(1f))
                action?.invoke()
            }
            Spacer(Modifier.height(12.dp))
            content()
        }
    }
}

/**
 * The month day by day: revenue (takings, or the target faded) and labour cost (clocked, or
 * planned faded) as bars, the labour share (SH-95: of income with a commission, of sales without)
 * as a line against the workplace's labour target ([labourTargetPercent], else 30 %).
 */
@Composable
fun RevenueLaborChartCard(
    days: List<DayTrend>,
    currency: String,
    modifier: Modifier = Modifier,
    labourTargetPercent: Double? = null,
    /** The plot's height: smaller where the chart sits beside a table, larger when shown full size. */
    chartHeight: Dp = 200.dp,
    action: (@Composable () -> Unit)? = null
) {
    if (days.isEmpty()) return
    val month = days.first().date
    val revenueLabel = stringResource(Res.string.chart_revenue)
    val laborLabel = stringResource(Res.string.chart_labor_cost)
    val base = if (days.any { it.labourBase == LabourBase.INCOME }) LabourBase.INCOME else LabourBase.SALES
    val shareLabel = stringResource(labourShareChartRes(base))
    val reference = labourTargetPercent ?: LABOR_TARGET_PERCENT
    val planSuffix = stringResource(Res.string.chart_plan_suffix)
    ChartCard(stringResource(Res.string.chart_revenue_labor_title, "${monthName(month.month.number)} ${month.year}"), modifier, action) {
        BoxWithConstraints {
            BarLineChart(
                xLabels = days.map { it.date.day.toString() },
                bars = listOf(
                    ChartBars(revenueLabel, DarkGold, days.map { it.revenueActual ?: it.revenuePlan }, days.map { it.revenueActual == null }),
                    ChartBars(laborLabel, DeepNavy, days.map { it.laborActual ?: it.laborPlanned }, days.map { it.laborActual == null })
                ),
                line = ChartLine(shareLabel, LaborShareRed, days.map { it.laborPercent }, ::percent, minAxisMax = shareAxisMax(reference), reference = reference),
                axisFormat = ::compactAmount,
                initialSelection = todayIndex(days.map { it.date }),
                labelEvery = labelStep(maxWidth, days.size),
                height = chartHeight
            ) { i ->
                val day = days[i]
                Column {
                    Text(dayLabel(day.date), style = MaterialTheme.typography.labelMedium, color = DeepNavy.copy(alpha = 0.6f))
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                        ChartFigure(
                            revenueLabel + if (day.revenueActual == null) " $planSuffix" else "",
                            formatMoney(day.revenueActual ?: day.revenuePlan, currency), DarkGold
                        )
                        ChartFigure(
                            laborLabel + if (day.laborActual == null) " $planSuffix" else "",
                            formatMoney(day.laborActual ?: day.laborPlanned, currency), DeepNavy
                        )
                        ChartFigure(shareLabel, day.laborPercent?.let(::percent) ?: "–", LaborShareRed)
                    }
                }
            }
        }
    }
}

/** Revenue and labour cost per month with the labour share, up to six months. */
@Composable
fun LaborTrendChartCard(months: List<MonthTrend>, currency: String, modifier: Modifier = Modifier, labourTargetPercent: Double? = null) {
    if (months.size < 2) return // one month is already on the KPI card
    val revenueLabel = stringResource(Res.string.chart_revenue)
    val laborLabel = stringResource(Res.string.chart_labor_cost)
    val base = if (months.any { it.labourBase == LabourBase.INCOME }) LabourBase.INCOME else LabourBase.SALES
    val shareLabel = stringResource(labourShareChartRes(base))
    val reference = labourTargetPercent ?: LABOR_TARGET_PERCENT
    val names = months.map { monthName(it.month).take(3) }
    ChartCard(stringResource(Res.string.chart_labor_trend_title), modifier) {
        BarLineChart(
            xLabels = names,
            bars = listOf(
                ChartBars(revenueLabel, DarkGold, months.map { it.revenue }),
                ChartBars(laborLabel, DeepNavy, months.map { it.laborCost })
            ),
            line = ChartLine(shareLabel, LaborShareRed, months.map { it.laborPercent }, ::percent, minAxisMax = shareAxisMax(reference), reference = reference),
            axisFormat = ::compactAmount,
            height = 180.dp
        ) { i ->
            val m = months[i]
            Column {
                Text("${monthName(m.month)} ${m.year}", style = MaterialTheme.typography.labelMedium, color = DeepNavy.copy(alpha = 0.6f))
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                    ChartFigure(revenueLabel, formatMoney(m.revenue, currency), DarkGold)
                    ChartFigure(laborLabel, formatMoney(m.laborCost, currency), DeepNavy)
                    ChartFigure(shareLabel, m.laborPercent?.let(::percent) ?: "–", LaborShareRed)
                }
            }
        }
    }
}

/** Planned and worked (clocked) hours per day of the month. */
@Composable
fun HoursChartCard(days: List<DayHours>, modifier: Modifier = Modifier) {
    if (days.isEmpty() || days.all { it.planned == 0.0 && it.worked == 0.0 }) return
    val month = days.first().date
    val plannedLabel = stringResource(Res.string.chart_hours_planned)
    val workedLabel = stringResource(Res.string.chart_hours_worked)
    fun hours(v: Double) = "${formatDecimal(v, if (v % 1.0 == 0.0) 0 else 1, decimalSeparator = ',')} h"
    ChartCard(stringResource(Res.string.chart_hours_title, "${monthName(month.month.number)} ${month.year}"), modifier) {
        BoxWithConstraints {
            BarLineChart(
                xLabels = days.map { it.date.day.toString() },
                bars = listOf(
                    ChartBars(plannedLabel, DarkGold, days.map { it.planned }),
                    ChartBars(workedLabel, DeepNavy, days.map { it.worked })
                ),
                axisFormat = { "${it.toInt()}" },
                initialSelection = todayIndex(days.map { it.date }),
                labelEvery = labelStep(maxWidth, days.size),
                height = 160.dp
            ) { i ->
                val day = days[i]
                Column {
                    Text(dayLabel(day.date), style = MaterialTheme.typography.labelMedium, color = DeepNavy.copy(alpha = 0.6f))
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                        ChartFigure(plannedLabel, hours(day.planned), DarkGold)
                        ChartFigure(workedLabel, hours(day.worked), DeepNavy)
                    }
                }
            }
        }
    }
}
